import React, { useEffect, useMemo, useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { notify } from '@/components/ui/notify';
import {
  fetchMarketIgnore,
  overrideMarketIgnore,
  MarketModuleOffError,
} from '@/lib/marketExperienceApi';
import {
  asList,
  formatEnrolment,
  formatIgnoreReturns,
  formatIgnoreReasonLabel,
  getIgnoreReasonMeta,
  ignoreReasonChipCounts,
  rowMatchesIgnoreReason,
  mktLabel,
} from '@/lib/marketExperience';
import { useTranslation } from '@/components/utils/translations';
import MarketHelpLabel from './MarketHelpLabel';

function ReasonBlock({ reason, accountId, busyKey, onOverride }) {
  const { t } = useTranslation();
  const meta = getIgnoreReasonMeta(reason?.reason);
  const label = formatIgnoreReasonLabel(reason?.label || reason?.reason);
  const returnsLine = formatIgnoreReturns(reason?.returns, reason?.returns_note, t);
  const overriding = busyKey === `${accountId}:${reason?.reason}`;

  return (
    <div className="rounded-md border border-gray-100 bg-gray-50/80 px-3 py-2.5 space-y-1.5">
      <span className={`inline-flex text-[11px] font-medium px-1.5 py-0.5 rounded ${meta.className}`}>
        {mktLabel(t, label)}
      </span>
      {reason?.quote ? (
        reason.source_url ? (
          <a
            href={reason.source_url}
            target="_blank"
            rel="noreferrer"
            className="block text-sm text-[#0A1A2F] leading-snug hover:text-[#00C2C2]"
          >
            “{reason.quote}”
          </a>
        ) : (
          <p className="text-sm text-[#0A1A2F] leading-snug">“{reason.quote}”</p>
        )
      ) : reason?.source_url ? (
        <a
          href={reason.source_url}
          target="_blank"
          rel="noreferrer"
          className="text-xs text-[#00C2C2] hover:underline"
        >
          {t('source')}
        </a>
      ) : null}
      {returnsLine ? (
        <p className="text-xs text-[#5C6B7A]">{returnsLine}</p>
      ) : null}
      {reason?.overridable ? (
        <div className="flex flex-col sm:flex-row sm:items-center gap-2 pt-1">
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-7 text-xs shrink-0"
            disabled={Boolean(busyKey)}
            onClick={() => onOverride?.(accountId, reason)}
          >
            {overriding ? t('mktMoving') : t('mktMoveBackToMyList')}
          </Button>
          {reason.override_note ? (
            <p className="text-[11px] text-[#5C6B7A] leading-snug">{reason.override_note}</p>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

export default function MarketIgnore({ tenantId, lens, countryFilter = 'all' }) {
  const { t } = useTranslation();
  const [payload, setPayload] = useState(null);
  const [unavailable, setUnavailable] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reasonFilter, setReasonFilter] = useState('all');
  const [busyKey, setBusyKey] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);

  const country = countryFilter === 'all' ? undefined : countryFilter;

  useEffect(() => {
    let cancelled = false;
    if (!tenantId || !lens) {
      setLoading(false);
      return undefined;
    }
    setLoading(true);
    setError(null);
    setUnavailable(false);
    fetchMarketIgnore(tenantId, lens, country)
      .then((data) => {
        if (!cancelled) setPayload(data);
      })
      .catch((e) => {
        if (cancelled) return;
        if (e instanceof MarketModuleOffError || e?.status === 404) {
          setUnavailable(true);
          setPayload(null);
        } else {
          setError(e?.message || 'Could not load ignored accounts');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [tenantId, lens, country, reloadKey]);

  const allRows = useMemo(() => payload?.rows || [], [payload]);
  const rows = useMemo(() => {
    if (reasonFilter === 'all') return allRows;
    return allRows.filter((row) => rowMatchesIgnoreReason(row, reasonFilter));
  }, [allRows, reasonFilter]);

  const reasonChips = useMemo(
    () => Object.entries(ignoreReasonChipCounts(allRows))
      .map(([key, count]) => ({ key, count: Number(count) || 0 }))
      .filter((c) => c.count > 0)
      .sort((a, b) => b.count - a.count),
    [allRows],
  );

  const handleOverride = async (accountId, reason) => {
    if (!tenantId || !accountId || !lens) return;
    const key = `${accountId}:${reason?.reason}`;
    setBusyKey(key);
    try {
      await overrideMarketIgnore(
        tenantId,
        accountId,
        { reason: reason?.reason },
        lens,
      );
      notify({ title: t('mktMovedBackToYourList'), variant: 'success' });
      setReloadKey((k) => k + 1);
    } catch (e) {
      notify({
        title: t('mktCouldNotMoveAccountBack'),
        description: e?.message || t('mktPleaseTryAgain'),
        variant: 'destructive',
      });
    } finally {
      setBusyKey(null);
    }
  };

  if (loading) return <div className="h-64 bg-gray-100 rounded-md animate-pulse" />;
  if (error) {
    return <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">{error === 'Could not load ignored accounts' ? t('mktCouldNotLoadIgnored') : error}</div>;
  }
  if (unavailable) {
    return (
      <Card className="border-0 shadow-md">
        <CardContent className="p-6 space-y-2">
          <h2 className="text-lg font-semibold text-[#0A1A2F]">{t('mktIgnore')}</h2>
          <p className="text-sm text-[#5C6B7A]">
            {t('mktIgnoreNotSwitchedOn')}
          </p>
        </CardContent>
      </Card>
    );
  }

  const total = allRows.length;
  const addressable = payload?.addressable;
  const note = payload?.note;

  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
          <h2 className="text-lg font-semibold text-[#0A1A2F]">
            <MarketHelpLabel
              label={t('mktIgnore')}
              hint={note || t('mktIgnoreHint')}
            />
          </h2>
          <p className="text-sm text-[#5C6B7A]">
            {addressable != null
              ? t('mktNOfTotalSetAside', { n: total, total: Number(addressable).toLocaleString() })
              : t('mktNSetAside', { n: total })}
          </p>
        </div>
        {reasonChips.length > 0 ? (
          <div className="flex flex-wrap gap-1.5">
            <button
              type="button"
              onClick={() => setReasonFilter('all')}
              className={`text-xs px-2.5 py-1 rounded-full ${
                reasonFilter === 'all' ? 'bg-[#0A1A2F] text-white' : 'bg-gray-100 text-[#0A1A2F]'
              }`}
            >
              {t('all')} · {total}
            </button>
            {reasonChips.map((chip) => {
              const meta = getIgnoreReasonMeta(chip.key);
              const label = formatIgnoreReasonLabel(chip.key);
              return (
                <button
                  key={chip.key}
                  type="button"
                  onClick={() => setReasonFilter(chip.key)}
                  className={`text-xs px-2.5 py-1 rounded-full ${
                    reasonFilter === chip.key
                      ? 'bg-[#0A1A2F] text-white'
                      : `${meta.className}`
                  }`}
                >
                  {mktLabel(t, label)} · {chip.count}
                </button>
              );
            })}
          </div>
        ) : null}
      </div>

      <Card className="border-0 shadow-md">
        <CardContent className="p-4 md:p-6">
          {rows.length === 0 ? (
            <p className="text-sm text-[#5C6B7A] py-8 text-center">
              {note
                || (reasonFilter !== 'all'
                  ? t('mktNoAccountsWithReason')
                  : t('mktNoAccountsSetAside'))}
            </p>
          ) : (
            <ul className="space-y-4">
              {rows.map((row) => {
                const reasons = asList(row.reasons);
                const fallbackNote = reasons.find((r) => r?.returns_note)?.returns_note;
                const rowReturns = formatIgnoreReturns(row.returns, fallbackNote, t);
                return (
                  <li
                    key={row.account_id || row.account_name}
                    className="rounded-lg border border-gray-200 bg-white p-4 space-y-3"
                  >
                    <div className="flex flex-wrap items-start justify-between gap-2">
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-[#0A1A2F]">
                          {row.account_name || row.account_id}
                        </p>
                        <p className="text-[11px] text-[#5C6B7A] mt-0.5">
                          {[row.country, row.sector, row.enrolment != null ? formatEnrolment(row.enrolment, row.enrolment_measure) : null]
                            .filter(Boolean)
                            .join(' · ')}
                          {row.financial_capacity_band
                            ? ` · ${t('mktBandCapacity', { band: row.financial_capacity_band })}`
                            : ''}
                        </p>
                      </div>
                      {rowReturns ? (
                        <p className="text-xs text-[#5C6B7A] shrink-0">{rowReturns}</p>
                      ) : null}
                    </div>
                    <div className="space-y-2">
                      {reasons.map((reason, i) => (
                        <ReasonBlock
                          key={`${reason.reason || i}-${i}`}
                          reason={reason}
                          accountId={row.account_id}
                          busyKey={busyKey}
                          onOverride={handleOverride}
                        />
                      ))}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
